<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useFetch } from '@/modules/core/composables/useFetch'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { VLoadingSection } from '@/modules/shared/components'
import { useAuthStore } from '../stores/auth-store'
import AuthLayout from '../layouts/auth-layout.vue'

//------------------------------------------------------------------------------
// Types
//------------------------------------------------------------------------------

type VerifyEmailResponse = {
  data: {
    access_token: string
  }
}

//------------------------------------------------------------------------------
// Verify email
//------------------------------------------------------------------------------

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const {
  organization,
  token,
  type: registrationType,
} = useRoute().query as {
  organization: string
  token: string
  type: 'company' | 'investor' | 'group'
}

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const loadingTitle = ref('Verifying Token')
const loadingDescription = ref('Please wait while we verify your token.')
const isTokenVerificationFailed = ref(false)

const updateLoadingMessage = async (title: string, description: string) => {
  loadingTitle.value = title
  loadingDescription.value = description
  await new Promise((resolve) => setTimeout(resolve, 2000))
}

const verifyEmail = async () => {
  try {
    const result = await useFetch('/v4/auth/verify-email').post<VerifyEmailResponse>({ token })
    authStore.access_token = result.data.access_token
  } catch {
    isTokenVerificationFailed.value = true
  }
}

const consolidateIndividualAccess = async () => {
  try {
    await useFetch('/v4/auth/consolidate-individual').post({ slug: organization })
  } catch (e) {
    console.error('Failed to consolidate investor access to group:', e.message)
  }
}

const handleRedirect = async () => {
  if (authStore.isInvestor) {
    const baseUrl = import.meta.env.VITE_VENTURE360_REDIRECT_URL
    const slugParam = organization ? `slug=${organization}` : ''
    const queryParams = [slugParam].filter((param) => param).join('&')

    await updateLoadingMessage(
      'Accessing your account details',
      'Please wait while we prepare your account information.',
    )
    window.location.href = `${baseUrl}/user?${queryParams}`
    return
  }

  let routeName = ''
  switch (registrationType) {
    case 'company':
      routeName = 'public.companies.apply'
      break

    case 'investor':
      routeName = 'public.investors.apply'
      break

    case 'group':
      routeName = 'public.group-users.apply'
      break

    default:
      break
  }
  await updateLoadingMessage('Token Verified!', 'Redirecting to complete your account setup.')
  router.push({ name: routeName, params: { slug: organization } })
}

onMounted(async () => {
  showSkeleton()
  await verifyEmail()

  if (isTokenVerificationFailed.value) {
    hideSkeleton()
    return
  }

  await authStore.refresh(authStore.access_token, { params: { slug: organization } })
  await consolidateIndividualAccess()
  await authStore.refresh(authStore.access_token, { params: { slug: organization } })
  await handleRedirect()
  hideSkeleton()
})
</script>

<template>
  <AuthLayout>
    <template v-slot:icon>
      <svg width="72" height="96" viewBox="0 0 72 96" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M63 39H9C5.68629 39 3 41.6863 3 45V87C3 90.3137 5.68629 93 9 93H63C66.3137 93 69 90.3137 69 87V45C69 41.6863 66.3137 39 63 39Z"
          fill="#111827"
          stroke="#111827"
          stroke-width="6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M15 39V24C15 12.4 24.4 3 36 3C47.56 3 57 12.4 57 24V39"
          stroke="#111827"
          stroke-width="6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path d="M36 60V72" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </template>

    <div v-if="isTokenVerificationFailed">
      <h1 class="text-3xl leading-9 text-gray-900">Verification Failed!</h1>
      <div class="mt-6 rounded bg-red-200 p-4">
        Sorry, the token you provided is invalid or has expired. Please try again or contact support for assistance.
      </div>
    </div>
    <div v-else class="">
      <VLoadingSection :title="loadingTitle" :description="loadingDescription" />
    </div>
  </AuthLayout>
</template>
